/**
 * The message channel between the gateway's iframe and this page.
 *
 * Kept as plain functions with no React and no DOM so the security-carrying part of the
 * payment flow can be tested directly -- see gateway.test.js.
 *
 * ## Who actually sends us the message
 *
 * Not the gateway. The gateway redirects its own frame to `/paymentreq/success/<hash>` or
 * `/paymentreq/fail/<hash>`, which are served by the linet app; those two pages post to
 * `window.top`. So the origin to trust is the linet app's, not the card processor's.
 *
 * That origin comes from the server (`message_origin` on the load response, built from the
 * same `commandUrl` the success and fail URLs are built from) rather than from this page's
 * guess at its API host. The page guesses by sniffing its own hostname; the moment the
 * configured `commandUrl` is anything other than that guess, every payment would stop
 * confirming -- silently, because a rejected message reports nothing anywhere.
 */

/**
 * How long a minted gateway session is worth showing.
 *
 * The gateways expire their own sessions on their own schedule and without telling us; a form
 * left open past that point accepts a card number and then fails at the worst moment. Twelve
 * minutes is under every gateway's window, so the page expires first and can say so.
 */
export const SESSION_TTL_MS = 12 * 60 * 1000

/** The two codes success.php and fail.php send. Nothing else advances the step. */
const CODES = {
  '000': 'success',
  '999': 'failure',
}

/**
 * Reduce a configured URL to a bare origin for comparison against `event.origin`, which is
 * always scheme + host + port and never carries a path or a trailing slash.
 *
 * Returns '' rather than throwing: a bad value must fail the origin check, not the render.
 */
export const originOf = (url) => {
  if (!url || typeof url !== 'string') {
    return ''
  }

  try {
    return new URL(url).origin
  } catch (e) {
    return ''
  }
}

/**
 * What a `message` event means, or null if it means nothing to us.
 *
 * Fails closed at every step. `window` receives messages from every frame on the page, from
 * anything that framed it, and from browser extensions and dev tooling; "the payment
 * succeeded" must be a sentence only one origin can say.
 *
 * @param {MessageEvent} event
 * @param {string} allowedOrigin  exact origin, from the server. '' and '*' match nothing.
 * @param {string} expectedHash   this attempt's hash; a payload naming another is ignored.
 * @returns {'success'|'failure'|null}
 */
export const parseGatewayMessage = (event, allowedOrigin, expectedHash) => {
  if (!event || !allowedOrigin || allowedOrigin === '*') {
    return null
  }

  if (event.origin !== allowedOrigin) {
    return null
  }

  let payload = event.data

  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload)
    } catch (e) {
      // Not ours. Dev tooling and extensions post plenty of traffic that is not JSON.
      return null
    }
  }

  if (!payload || typeof payload !== 'object') {
    return null
  }

  // A payload with no hash is from a row minted before the hash was added; the page it is
  // talking to has exactly one attempt open, so accept it. A payload naming a DIFFERENT
  // attempt is dropped -- pay, decline, retry means two attempts and one stale '000'.
  if (payload.hash && payload.hash !== expectedHash) {
    return null
  }

  return CODES[payload.code] || null
}

/**
 * Has the minted session outlived its usefulness?
 *
 * `mintedAt` null means no session has been minted yet, which is not the same as an expired
 * one -- there is nothing to expire, so it is not stale.
 */
export const isSessionStale = (mintedAt, now) => {
  if (!mintedAt) {
    return false
  }

  return now - mintedAt > SESSION_TTL_MS
}

/**
 * `status` on the state endpoint. Mirrors PaymentRequest's constants.
 *
 * CLAIMED (5) and CLOSED (20) are not written by the backend yet -- they arrive with the
 * schema change -- but the page must already handle a status it does not recognise, because
 * a page cached in a payer's browser outlives the deploy that introduces one.
 */
export const STATUS_PAID = 10

/** Confirming: every 2s, for up to 30s, once the gateway says the card was approved. */
export const CONFIRM_POLL_MS = 2000
export const CONFIRM_TIMEOUT_MS = 30000

/**
 * The fallback poll, running from the moment the frame is shown.
 *
 * The message channel can simply not arrive: a gateway that opens its own window rather than
 * redirecting the frame, a browser extension eating postMessage, a `message_origin` that does
 * not match. Polling slowly in the background means the payment still confirms itself.
 */
export const BACKGROUND_POLL_MS = 10000

/** How long the iframe gets to load before the watchdog calls it a failure. */
export const IFRAME_WATCHDOG_MS = 20000

/** Has the server confirmed the document exists? */
export const isPaid = (state) => !!state && Number(state.status) === STATUS_PAID
